import QueryActions from "./QueryActions";
import Scene from './Scene.js';
import UserEvents from "./UserEvents";
import SafeMargin from "./SafeMargin";
import Message from "./Message";
import ScrollListener from "./ScrollListener";
import ScrollContext from "./ScrollContext";
import Replaceables from './Replaceables';
import {checkBrowser, constructSceneUUID, debounce, getElementOffsetInScene, isElementInViewport} from "./Utils"
import CONSTANTS, {LAYOUT_DESKTOP, LAYOUT_MOBILE, STATUS} from "./Constants";
import Links from "./Links";
import SceneTransitions from "./sceneMotion/sceneTransitions";
import ScrollingSceneEffects from "./sceneMotion/ScrollingSceneEffects";
import PopupAccessibility from "./popupAccessibility";
import {Howl} from 'howler';

export default class Spread {

    constructor(spread_id, settings, options = {} ){
        this.options = options;

        this.spread_id = spread_id;
        this.status = CONSTANTS.STATUS.BLANK;
        this.node = options.shadowNode || document.getElementById("spread_"+this.spread_id);
        this.root = options.shadowRoot || document
        this.settings = settings.settings;
        this.layoutScenes = {};
        this.scenes = [];
        this.scenesMap = new Map();
        this.scrolling_scenes = false;
        this.setSceneNavigationMode(options.sceneNavigationMode ?? "animated");
        this.setWheelNavigationEnabled(options.wheelNavigationEnabled ?? true);

        if(this.settings._spread_globals === undefined){
            this.settings._spread_globals = { spread_id:  spread_id };
        }

        //check if the spread container with the current spread_id exists
        const spreadElement = this.getSpreadElement(false);
        if( ! spreadElement ){
            throw new Error("target spread element not found!");
        }

        // no-mobile / no-desktop: from options or from class on the spread (so you can force layout in HTML)
        this.noMobile = (options.injected && options.injected.noMobile) || this.settings.scenes[LAYOUT_MOBILE].length === 0 && this.settings.noSceneFlag;
        this.noDesktop = (options.injected && options.injected.noDesktop) || this.settings.scenes[LAYOUT_DESKTOP].length === 0 && this.settings.noSceneFlag;
        if (this.noMobile) spreadElement.classList.add("no-mobile");
        if (this.noDesktop) spreadElement.classList.add("no-desktop");

        if (this.noMobile) {
            this.active_layout = LAYOUT_DESKTOP;
        } else if (this.noDesktop) {
            this.active_layout = LAYOUT_MOBILE;
        } else {
            this.active_layout = spreadElement.offsetWidth < 501 ? LAYOUT_MOBILE : LAYOUT_DESKTOP;
        }

        spreadElement.classList.add(`engine_${checkBrowser()}`);

        options.spreadOffset && this.setSpreadOffset(options.spreadOffset);

        //when the injected option is given set injected to true and add a class to the spread container
        if (options.injected) { // used in: inject, embed & singlepage modes
            this.injected = true;
            this.embedded = document.body.className.indexOf("embed") > -1; // .embed only set when embedded

            if (options.injected.fullHeight) { // true in all 3 modes
                this.fullHeight = true;

                if (options.injected.singlePage) { // only in embed & singlepage mode
                    spreadElement.classList.add("singlePage");
                }

                //size="in-ratio|auto-fill|max-height|max-width-height"
                switch (options.injected.size) {
                    case 'auto-fill':
                        spreadElement.style.setProperty('--maglrMaxHeight', spreadElement.offsetHeight+'px')
                        break
                    case 'max-height':
                    case 'max-width-height':
                        spreadElement.classList.add(options.injected.size)
                        break
                    case 'in-ratio':
                    default:
                        if (this.embedded || !options.injected.singlePage) {
                             spreadElement.classList.add("in-ratio")
                        }
                        break
                }

                if (spreadElement.offsetWidth > 0) {
                    spreadElement.style.setProperty('--maglrMaxWidth', `${spreadElement.offsetWidth}px`)
                }
            }
        }

        const scrollContainer = options.scrollContainer ?? options.injected?.scrollContainer;
        this.scrollContext = new ScrollContext(spreadElement, scrollContainer);
        if (this.scrollContext.element) {
            // Preserve the external-flow layout; only the scrollport changes.
            spreadElement.classList.add('scrollingContainer');
            this.updateScrollViewport();
        }

        //set html if it was given in the settings
        if(settings.html && !options.noHtml){
            spreadElement.innerHTML = settings.html;
        }

        // Only the option, not the window flag: the classic preview document still relies on
        // plugins/preview.js for this, and binding both would handle every click twice.
        if (options.editorPreview === true) {
            this.bindEditorPreviewLinks();
        }

        this.safemargin = new SafeMargin(this);
        this.gsapSceneTransitions = window.MaglrGsapSceneTransitions === true;
        this.popupA11y = new PopupAccessibility(this);
        this.sceneTransitions = new SceneTransitions(this);
        this.scrollingSceneEffects = new ScrollingSceneEffects(this);

        if (!this.noMobile && !this.noDesktop && Math.min(window.innerWidth, window.screen.width) < 501){
            this.active_layout = LAYOUT_MOBILE;

            // The screen.width always returns the width in portrait mode this causes landscape mobile to crash.
            // check if the orientation is landscape, if so set it back to layout 1 (unless no-desktop forces mobile).
            if (window.screen.width < 501 &&
                (
                    (!this.embedded && window.matchMedia("(orientation: landscape)").matches) ||
                    (this.embedded && Math.abs(window.orientation) === 90))
                )
            {
                this.active_layout = LAYOUT_DESKTOP;
            }
        }

        if ("IntersectionObserver" in window && !this.screenshotmode) {
            this.lazyVideoObserver = new IntersectionObserver((entries, observer) => {
                console.log('LAZY VIDEOS', entries)
                entries.forEach(function(video) {
                    if (video.isIntersecting) {
                        console.log('LAZY VIDEOS is intersecting', video)
                        for (let source in video.target.children) {
                            let videoSource = video.target.children[source];
                            if (typeof videoSource.tagName === "string" && videoSource.tagName === "SOURCE") {
                                videoSource.src = videoSource.dataset.src;
                            }
                        }

                        video.target.load();
                        video.target.classList.contains('maglr-autoplay') && video.target.dispatchEvent(new CustomEvent('triggerAutoplay'))
                        observer.unobserve(video.target);
                    }
                });
            });
        } else {
            this.lazyVideoObserver = false
        }
        // split video's into 2 separate array's based on layout type (1 | 2).
        this.videosList = {
            1 : [],
            2 : []
        };
        this.settings.videos.forEach((item) => {
            // Layout is the last character (`…_1` / `…_2`, including stack background `…_stackbg_1`).
            const layoutKey = item.slice(-1)
            if (this.videosList[layoutKey] === undefined) {
                console.warn('[Spread] Video id does not map to layout 1 or 2; skipping Videos init for:', item, 'inferredKey:', layoutKey)
                return
            }
            this.videosList[layoutKey].push(item)
        });

        //merge the replaceables from the publish settings with the resolved replaceables
        if(this.settings.replaceables || options.replaceables){
            this.options.replaceables = Replaceables.merge(options.replaceables, this.settings.replaceables);
        }

        this.status = CONSTANTS.STATUS.LOADED;
    }

    get hasScrollingScenes() {
        if (this.active_layout === LAYOUT_MOBILE) {
            return this.scrollingMobileScenes
        }

        return this.scrolling_scenes
    }

    createScenes(){

        const disableScrollFixedAnimations = this.injected && !!this.getSpreadElement(false).closest('.disable-fixed-animations')

        //check if the current active_layout already exists in the layoutScenes object. This means is was created before.
        if(this.layoutScenes[this.active_layout]){

            this.scenes = this.layoutScenes[this.active_layout].scenes;
            this.scenesMap = this.layoutScenes[this.active_layout].scenesMap;

        }else{
            //layout not yet created
            if (this.options.replaceables) {
                Replaceables.mergeGlobals(this.options.replaceables, this.getGlobalData())
                Replaceables.replace(this.options.replaceables, this.getSpreadElement(false), this.active_layout);
            }

            this.scenes = [...Array( this.settings.scenes[this.active_layout].length )];
            this.scenesMap = new Map();

            this.layoutScenes[this.active_layout] = {
                scenes : this.scenes,
                scenesMap : this.scenesMap,
            };

            if(this.settings.scenes[this.active_layout].length > 0){
                for(let i = 0; i < this.settings.scenes[this.active_layout].length; i++){
                    let uuid = this.settings.scenes[this.active_layout][i];
                    this.scenesMap.set(uuid, i);
                    if (disableScrollFixedAnimations) {
                        this.settings.animations[uuid].settings.disableScrollFixedAnimations = disableScrollFixedAnimations
                    }
                    this.scenes[i] = new Scene(this, uuid, this.settings.animations[uuid], this.videosList[this.active_layout], this.settings.scrubs?.[uuid], this.getThreeCanvasSettings(uuid), this.active_layout);
                }
            }else{
                console.log(`layout [${this.active_layout}] doesn't have scenes`);
            }
        }

    }

    getSceneByUuid(uuid){
        return this.scenes[this.scenesMap.get(uuid)];
    }

    /**
     * Return three canvas configs for a scene with spread data_domain attached.
     *
     * @param {string} sceneUuid
     * @returns {Array|undefined}
     */
    getThreeCanvasSettings(sceneUuid) {
        const canvases = this.settings.three?.[sceneUuid];
        if (!canvases?.length) {
            return undefined;
        }

        const dataDomain = this.settings.data_domain ?? '';
        return canvases.map((canvas) => ({
            ...canvas,
            data_domain: dataDomain,
        }));
    }

    /**
     * Main init function for all magazines. If the parameter settings is false dont do anything. this is the case when the
     * template is loaded in the editor.
     *
     * @method init
     * @public
     */
    init(){
        console.log("Spread INIT", window.innerWidth, window.innerHeight, screen.width, screen.height);

        let actions = QueryActions.analyzeQueryString(this.getSpreadElement(false), this.active_layout);
        Message.sendMessage({message: "PAGE_LOADED"}, this.spread_id);

        if(this.options.hasOwnProperty("scene")){
            actions.set("sceneDeepLink", this.options.scene);
        }

        if (actions.get('disabledAnimations') && this.settings && this.settings.animations) {
            Object.entries(this.settings.animations).forEach(([sceneId, value]) => {
                this.settings.animations[sceneId].children = null
            })
        }

        //determine if the desktop version is scrolling or transitioning scenes
        let desktop = this.runSelector(".layout.desktop");
        this.scrolling_scenes = desktop.hasClass("scrolling-scenes");
        this.scrollingMobileScenes = !this.runSelector('.layout.mobile').hasClass("transitioning-scenes");
        if(this.scrolling_scenes){
            this.sceneSnap = true;
            if( desktop.data("scenesnap") !== undefined ){
                this.sceneSnap = desktop.data("scenesnap");
            }
        }

        this.mousewheel_enabled = desktop.data("mousewheel") || false;
        this.screenshotmode = desktop.hasClass("screenshotmode");
        this.pdfmode = this.getSpreadElement(true).hasClass("pdfmode");

        this.createScenes();

        //bind safeMargin events
        this.safemargin
            // .on(CONSTANTS.EVENTS.BEFORE_RESIZE, () => {
            //     this.scenes.forEach(scene => {
            //         scene.status > CONSTANTS.STATUS.SHOWN && scene.animations.onWindowResize()
            //         // scene.status === CONSTANTS.STATUS.STARTED && scene.animations.onWindowResize()
            //     })
            // })
            .on(CONSTANTS.EVENTS.WINDOW_SIZE_CHANGED, () => {
                this.clearSizes();
            })
            .on(CONSTANTS.EVENTS.RESIZE_DONE, debounce(() => {
                this.resized();
                this.scrollingSceneEffects.refresh();

                if (this.injected) {
                    if(!this.scrolling_scenes && this.active_layout === LAYOUT_DESKTOP && this.currentScene){
                        //injected transitioning scenes spreads don't have height,
                        this.getSpreadElement(true).css({height: this.currentScene.getSceneElement(true).data("height")})
                    }

                    const spreadElement = this.getSpreadElement(false)
                    if (spreadElement.offsetWidth > 0) {
                        spreadElement.style.setProperty('--maglrMaxWidth', `${spreadElement.offsetWidth}px`)
                    }
                    if (!this.embedded) {
                        spreadElement.style.setProperty('--offset', spreadElement.offsetLeft+'px')
                        // spreadElement.style.setProperty('--maglrMaxWidth', spreadElement.offsetWidth+'px')
                        if (this.options.injected.size === 'auto-fill') {
                            spreadElement.style.setProperty('--maglrMaxHeight', spreadElement.offsetHeight+'px')
                        }
                    }
                }

                if(this.embeddedWithJsControl){
                    let message = {
                        height : this.getScrollHeight()
                    }

                    if(this.injected && this.embedded && this.embeddedType === CONSTANTS.EMBEDTYPES.FULLHEIGHT && this.embeddedSizeSetting === 'width-height'){
                        if(this.currentScene && this.currentScene.getSizeData()){
                            const sizeData = this.currentScene.getSizeData();
                            message.width = sizeData.unscaledWidth || false; // TODO: deze hebben we niet meer
                            message.switchPoint = sizeData.width || false // TODO: deze hebben we niet meer
                        }
                    }
                    Message.sendEmbedMessage( "SIZE_CHANGED", message);
                }

            }, 200, false))
            .on(CONSTANTS.EVENTS.LAYOUT_CHANGED, () => {
                // an open popup does not survive the scene rebuild below; tear its
                // a11y state down first so its listeners/EscapeLayerStack entry
                // are not orphaned on the discarded layout's DOM
                this.popupA11y.destroy();
                this.scenes.forEach(scene => scene.animations.disableScrollTriggers())
                // this.scenes.forEach(scene => scene.animations.killScrollTriggers())
                this.stopScenes(false);
                this.createScenes();
                this.safemargin.onWindowResizeEvent(null, {force:true});
                this.resized();
                this.currentScene = this.initFirstScene();
                // this.scenes.forEach(scene => scene.animations.init())

                if (this.status >= CONSTANTS.STATUS.PAUSED) {
                    if(this.currentScene){
                        this.start();
                        this.bindScroll();
                    }else{
                        console.warn("NO_SCENES");
                    }
                }
            })
        ;

        let width = this.getWindowWidth();
        this.safemargin.onWindowResizeEvent(null, {force:true});
        if(width !== this.getWindowWidth(true)){ //if window with is not equal to the previous width resize again. this can happen when scrollbars appear
            this.safemargin.onWindowResizeEvent(null, {force:true});
        }

        // this.scenes.forEach(scene => scene.animations.init())

        //check if there are scenes, can be on mobile.
        if( this.scenes.length === 0) {
            console.log("no scenes");
            this.safemargin.bind();
            return this;
        }

        let first_scene, scene_index;
        if( actions.has("sceneDeepLink") ){
            let scene = this.completeSceneUUID(actions.get("sceneDeepLink"));

            scene_index = this.scenesMap.get(scene);

            if( scene_index >= 0){


                //init the DeepLink scene and the sticky scene
                first_scene = this.scenes[scene_index];

                if (first_scene.isPopup) {
                    this.scenes.forEach(scene => scene.setTopForPopup())
                    this._scrollBeforePopup = 0
                } else if (this.hasScrollingScenes && this.scenes[scene_index].getSceneElement().closest('.preloadedSpread').length === 0 ) {
                    this.scrollToScene(this.scenes[scene_index].getSceneElement(), {duration: 1, suppress:true});
                }

                first_scene.init();
                if( scene_index > 0 && this.scenes[0].sticky ){
                    this.scenes[0].init();
                }
            }
        } else if (actions.has('elementDeepLink')) {
            const elementDeepLink = actions.get("elementDeepLink")
            if (elementDeepLink) {
                const sceneElement = elementDeepLink.closest('.scene')
                scene_index = this.scenesMap.get(sceneElement.id);
                first_scene = this.scenes[scene_index];
                if (this.scrollContext.element) {
                    this.scrollContext.scrollTo(this.scrollContext.offsetOf(elementDeepLink));
                } else {
                    window.scrollTo(0, (getElementOffsetInScene(elementDeepLink) * first_scene.getSizeData().scale || 1) + sceneElement.offsetTop)
                }
            }
        }

        if( ! first_scene || first_scene.sticky ){
           first_scene = this.initFirstScene();
        }

        this.currentScene = first_scene.setFirstScene();

        this.setSceneLinkActive();

        if( (this.screenshotmode || this.pdfmode) && window.MaglrPrint){
            this.scenes.forEach( (scene)=> {
               scene.init();
            });
            window.MaglrPrint.setStatus('LOADED');
        }

        //chainable
        return this;
    }

    resized(){
        if(!this.scrolling_scenes){
            if (this.active_layout === LAYOUT_MOBILE) {
                this.getSpreadElement(false).style.removeProperty('height');
            }
        }

        this.scenes.forEach( (scene)=> {
            scene.resized();
        });

        const scrollHeight = (this.scrollContext.element || document.documentElement).scrollHeight;
        this._scrollHeight = {
            totalHeight : scrollHeight,
            viewport: this.getWindowHeight(),
            onePercent: scrollHeight * 0.01
        };
    }

    initFirstScene(){
        if( this.scenes.length > 0 ){
            //init the first scene and the sticky scene
            let first_scene = this.scenes[0].init();
            if( first_scene.sticky ){
                first_scene = this.scenes[1].init();
            }
            return first_scene;
        }
        return false;
    }

    show(){
        if( this.status < CONSTANTS.STATUS.SHOWN){
            this.getSpreadElement().addClass("shown");
            this.status = CONSTANTS.STATUS.SHOWN;

            this.ready();
        }
        //fallback for if we send another page show and page is already ready.
        else if (this.status > CONSTANTS.STATUS.SHOWN){
            //TODO: dit zorgt bij embeds voor 4x de page_ready message.
            Message.sendMessage("PAGE_READY", this.spread_id);
        }

        // this.scenes.forEach((scene) => scene.hasStickies() && scene.calculateStickyFallback());
        // let scroll_data = {
        //     top : this.getScrollTop()
        // };
        // scroll_data.bottom = scroll_data.top + this.getWindowHeight();
        // this._setStickiesActive(scroll_data);

        //chainable
        return this;
    }


    ready(){
        if(this.status === CONSTANTS.STATUS.SHOWN){

            this.status = CONSTANTS.STATUS.READY;
            Message.sendMessage("PAGE_READY", this.spread_id);

            if( (this.screenshotmode || this.pdfmode) && window.MaglrPrint){
                this.resized()
                window.MaglrPrint.setStatus('READY');
            }
        }

        return this;
    }

    start(data = {}){
        if(this.status !== CONSTANTS.STATUS.STARTED){

            if( ! this.currentScene ){
                //check if embedded and the width of the element is 0. If so the container (or a parent) is probably display none.
                this.status = CONSTANTS.STATUS.PAUSED;
                this.getSpreadElement().removeClass('stopped').addClass('started');

                if(this.embedded && this.getSpreadElement(false).offsetWidth === 0){
                    throw {type: CONSTANTS.ERRORS.NOT_VISIBLE, error: "Spread is not visible", data: this.scenes};
                }else{
                    throw {type: CONSTANTS.ERRORS.NO_SCENES, error: "No current scene", data: this.scenes};
                }
            }

            if(data.scene){
                let compatible_uuid = this.completeSceneUUID(data.scene);
                if( this.currentScene.uuid !== compatible_uuid){
                    let scene = this.scenes[this.scenesMap.get(compatible_uuid)];
                    if(scene){
                        this.currentScene.removeFirstScene();

                        this.setCurrentSceneByUUID(compatible_uuid);
                    }
                }
                if (this.hasScrollingScenes) {
                    this.scrollToScene(this.currentScene.getSceneElement(), {duration: 1, suppress:true});
                }
            }

            data.offset && this.setSpreadOffset(data.offset);

            this.status = CONSTANTS.STATUS.STARTED;
            this.getSpreadElement().removeClass('stopped').addClass('started');
            !this.injected && checkBrowser() !== CONSTANTS.VENDORS.SAFARI && !(document.activeElement && document.activeElement.dataset.maglrconsent) && this.getSpreadElement(false).focus({preventScroll:true});


            //force a resize

            // this.safemargin.onWindowResizeEvent(null, {force:true}); // uitgezet op 11-05-2020 in de performance update
            this.safemargin.onWindowResizeEvent(null, {force:true});
            this.safemargin.bind();

            if ( this.active_layout === LAYOUT_DESKTOP) {
                this.startSpreadAudio(this.settings.spread_audio);
            }
            this.bindScroll();

            if( this.scenes[0].sticky ){
                this.scenes[0].start();
            }

            this.currentScene.start();

            // this.setSceneLinkActive();

            if(window.MaglrPrint){
                window.MaglrPrint.setReady(this.currentScene.uuid)
            }

            if (this.videosList[this.active_layout].length > 0) {
                this.getSpreadElement(true).on('keyup.a11y', (event) => {
                    if(event.key === 'Tab') {
                        this.getSpreadElement(true).off('keyup.a11y')
                        this.enterA11yMode()
                    }
                })
                if( this.videosList[this.active_layout].length <= 3 ){
                    //preload all video's for the current spread after the current scene is done
                    this.scenes.forEach((scene) => {
                        if(scene !== this.currentScene){
                            scene.videos && scene.videos.preloadVideo()
                        }
                    })
                }
            }

        }

        return this;
    }

    pause(){
        this.status = CONSTANTS.STATUS.PAUSED;
        this.unbindScroll();
        this.scenes.forEach((scene) => {
            scene.pause();
        });
    }

    stop(){
        this.safemargin.unbind();
        this.unbindScroll();
        this.stopLocalSpreadAudio();
        // an open popup left the background scenes position:fixed with inline
        // tops, the document scrolled to the spread top and the background inert;
        // close it for real so a later start() resumes on a regular scene
        this.sceneTransitions.abortPopup();
        this.runSelector(".spread").addClass("stopped").removeClass("started");

        this.stopScenes();

        this.status = CONSTANTS.STATUS.STOPPED;

        // this.reportScrollDepth();

        return this;
    }

    stopScenes(markCurrentAsFirst = true){
        this.scrollingSceneEffects.kill();

        if(this.scenes.length === 0){
            return false;
        }

        if(markCurrentAsFirst){
            let active_scene;
            if(this.currentScene){
                active_scene = this.currentScene;
            }else{
                //first has index 0 but when we have a sticky scene the first scene will be index 1
                active_scene = this.scenes[0];
                if(active_scene.sticky ){
                    active_scene = this.scenes[1];
                }
            }
            active_scene.setFirstScene();
        }


        this.scenes.forEach((scene )=>{
           scene.stop(true);
           scene.inViewport = false
           if(this.intersectionObserver){
                this.intersectionObserver.unobserve(scene.getSceneElement(false));
           }
        });
        this.scenes.forEach(scene => scene.animations.disableScrollTriggers())

        this.status = CONSTANTS.STATUS.PAUSED;
    }

    destroy(){
        if (this.destroyed) return;
        this.destroyed = true;
        this.status = CONSTANTS.STATUS.STOPPED;
        this.sceneTransitions.abortPopup();
        this.safemargin.unbind();
        this.popupA11y.destroy();
        this.sceneTransitions.killActiveTimeline();
        this.unbindScroll();
        this.stopLocalSpreadAudio();
        if (this.editorPreviewClickHandler) {
            this.root.removeEventListener("click", this.editorPreviewClickHandler, true);
        }

        [CONSTANTS.EVENTS.WINDOW_SIZE_CHANGED,CONSTANTS.EVENTS.RESIZE_DONE, CONSTANTS.EVENTS.LAYOUT_CHANGED].forEach( eventName => {
            this.safemargin.removeAllListeners(eventName);
        });

        if(this.intersectionObserver){
            this.intersectionObserver.disconnect();
        }
        if (this.lazyVideoObserver) this.lazyVideoObserver.disconnect();
        // Rotation can initialize both layouts. Dispose every scene once.
        const allScenes = new Set(this.scenes);
        Object.values(this.layoutScenes).forEach(layout => {
            layout.scenes.forEach(scene => allScenes.add(scene));
        });
        allScenes.forEach((scene )=>{
            // createScenes reserves slots before construction; failed init can leave gaps.
            if (!scene) return;
            scene.stop(true);
            scene.destroy();
        });
        this.getSpreadElement(true).off(".a11y");
        this.scenes = [];
        this.scenesMap.clear();
        this.layoutScenes = {};
        this.currentScene = null;
    }

    /**
     *
     * @param {Scene} scene
     * @param {boolean} stop_old stops the old currentScene
     */
    setCurrentScene(scene, stop_old = true){
        if(this.currentScene && scene !== this.currentScene){

            if(stop_old){
                this.currentScene.stop();
            }
            this.currentScene = scene;
            this.currentScene.setFirstScene().start();
            this.setSceneLinkActive();


            if(this.embeddedWithJsControl && this.embeddedType === CONSTANTS.EMBEDTYPES.FULLHEIGHT && !this.scrolling_scenes){
                Message.sendEmbedMessage( "NEW_SCENE", {
                    height : this.getScrollHeight(true)
                } );
            }

        }
    }

    setCurrentSceneByIndex(index){
        this.setCurrentScene(this.scenes[index]);
    }

    setCurrentSceneByUUID(uuid){
        if(this.currentScene && uuid !== this.currentScene.uuid){
            this.setCurrentSceneByIndex( this.scenesMap.get(uuid) );
        }
    }

    setCurrentSceneOnly(scene){
        if(this.currentScene && scene !== this.currentScene){
            this.currentScene = scene;
        }
    }

    notTheCurrentSceneAnymore(scene){
        if(this.currentScene && scene === this.currentScene){
            // a popup can have inViewport set by the intersectionObserver while it
            // overlays the page, but it only becomes current via openPopup — never
            // through the scroll-flow bookkeeping here
            let newCurrent = this.scenes.find((scene) => {
                return scene.inViewport && !scene.isPopup;
            })
            newCurrent && (this.currentScene = newCurrent)
        }
    }

    /**
     * Add the active class to all elements that link to the currentScene
     */
    setSceneLinkActive(){
        if (this.active_layout === LAYOUT_MOBILE) {
            return
        }
        //add active class to current-scene scene-links and remove the old.
        //links published with data-scene-link-no-active opted out of the active (hover) state
        this.runSelector("[data-scene-link]").removeClass("active").filter("[data-scene-link*='"+this.currentScene.uuid+"']:not([data-scene-link-no-active])").addClass("active");

        if(this.scenes[0].sticky && !this.hasLoopingScenes() ){
            if(this.currentScene === this.scenes[1]){ // current scene = first regular scene
                this.scenes[0].runSelector('.prev-scene-link').addClass("scene-link-disabled")
            }else{
                this.scenes[0].runSelector('.prev-scene-link').removeClass("scene-link-disabled")
            }
            if(this.currentScene === this.scenes[this.scenes.length-1]){ // current scene = last regular scene
                this.scenes[0].runSelector('.next-scene-link').addClass("scene-link-disabled")
            }else{
                this.scenes[0].runSelector('.next-scene-link').removeClass("scene-link-disabled")
            }
        }
    }

    /**
     * Get the window height.
     * @returns {Number}
     */
    getWindowHeight(){
        if (this.scrollContext.element) {
            const offset = this.getInterfaceOffset();
            return Math.max(0, this.scrollContext.height - offset.top - offset.bottom);
        }
        if(this.windowHeight){
            return this.windowHeight;
        }

        if( this.pdfmode ){
            this.windowHeight = 1102;
        }else{
            if(this.fullHeight){
                this.windowHeight = window.innerHeight; // TODO hier misschien op zoek gaan naar de 1e parent die scrollbaar/ een vaste hoogte heeft.
            }else{
                this.windowHeight = window.innerHeight - this.getInterfaceSettings().height - this.getSpreadOffset().top - this.getSpreadOffset().bottom;
            }
        }

        return this.windowHeight;
    }

    getWindowWidth(force){
        if(this.windowWidth && !force){
            return this.windowWidth;
        }

        if( this.pdfmode ){
            this.windowWidth = 1559;
        }else{
            this.windowWidth = this.getSpreadElement(false).offsetWidth;
        }

        return this.windowWidth;
    }

    clearSizes(){
        if( ! this.embeddedWithJsControl ){
            this.windowWidth = false;
            this.windowHeight = false;
        }
    }

    getScrollHeight(original = false){

        switch(this.embeddedType){
            case CONSTANTS.EMBEDTYPES.STATIC:
                return this.getSpreadElement(false).innerHeight;
            case CONSTANTS.EMBEDTYPES.FULLHEIGHT:
            default:
                if (this.active_layout === LAYOUT_MOBILE) {
                    return parseInt( this.getSceneContainer()[0].offsetHeight);
                }else{
                    //desktop
                    if( this.scrolling_scenes ){
                        return this.scenes.reduce( (accumulator, currentValue) => {
                            return accumulator + currentValue.getSizeData().height
                        }, 0 );
                    }else{
                        if( original ){
                            return parseInt(this.currentScene.getSceneElement(true).data('dimensions').height);
                        }else{
                            return this.currentScene.getSceneElement(true).data("height");
                        }
                    }
                }
        }

    }

    getSceneContainer( force ){
        let scrollable = false;
        if (this.active_layout === LAYOUT_MOBILE) {
            scrollable = this.runSelector(".layout.mobile");
        } else if (this.scrolling_scenes || force ) {
            scrollable = this.runSelector(".layout.desktop");
        }
        return scrollable;
    }

    getScrollContainer(for_listener){
        if (this.scrollContext.element) return $(this.scrollContext.element);
        //To animate the window scroll we need to return the html, body
        let selector = "html, body";

        //but for scroll listeners we need to return the window
        if(for_listener){
            selector = window;
        }
        return $(selector);
    }

    getScrollTop(){
        if (this.scrollContext.element) return this.scrollContext.scrollTop;
        return window.pageYOffset;
    }

    getAnimationScroller() {
        return this.scrollContext.target;
    }

    getScrollViewportTop() {
        return this.scrollContext.top;
    }

    updateScrollViewport() {
        if (!this.scrollContext.element) return;
        const node = this.getSpreadElement(false);
        node.style.setProperty('--fixed100vh', `${this.getWindowHeight()}px`);
        node.style.setProperty('--scroll-viewport-height', `${this.getWindowHeight()}px`);
        node.style.setProperty('--scroll-viewport-width', `${node.clientWidth}px`);
    }

    /**
     * Scene link function for when an element triggers it. Based on the currentTarget get the new link to go to and then
     * scroll or transition to it.
     *
     * @param {object} event
     * @public
     */
    _sceneLink(event){
        event.stopImmediatePropagation();
        const data = $(event.currentTarget).data("scene-link");
        this.sceneLink(data.scene, event.currentTarget);
    }

    popupLink (event) {
        this.setActivePopup($(event.currentTarget).data("popup-link"), event.currentTarget)
    }

    navigateByInput(direction){
        if (!this.scrolling_scenes && this.mousewheel_enabled && this.active_layout === LAYOUT_DESKTOP) {
            return this.sceneLink(direction);
        }
        return false;
    }

    /**
     * How an authored scene link moves the spread. "animated" and "direct" scroll or transition
     * here. "host" is for a host that owns the page's vertical scrolling while the page is not
     * being read (the verticalswipe viewer in browse mode): a link to a scrolling scene is then
     * reported as a `scene_link` message and left to the host, which opens the page on that scene
     * and moves it with its own mode (maglr-spread goToScene). Transitioning scenes still switch
     * here, directly, as they did in "direct".
     *
     * @param {"animated"|"direct"|"host"} mode
     */
    setSceneNavigationMode(mode) {
        if (!["direct", "animated", "host"].includes(mode)) {
            throw new TypeError(`Unknown scene navigation mode: ${mode}`);
        }
        this.sceneNavigationMode = mode;
        return this;
    }

    setWheelNavigationEnabled(enabled) {
        this.wheelNavigationEnabled = Boolean(enabled);
        return this;
    }

    /**
     * Scene link function for direct navigation to the given scene
     *
     * @param {string} scene - scene id or NEXT|PREV
     * @public
     */
    async sceneLink(scene, currentTarget, dispatchEventUpdate = true){
        let scene_container =  this.getSceneContainer(true),
            selector,
            send_update_event
        ;

        if(scene === "NEXT"){ // go to next scene, if no next go to first
            selector = this.currentScene.getSceneElement().nextAll(".scene:not(.popup_scene)").first();
            // selector = this.scenes.findIndex((scene) => { return scene === this.currentScene })
            if(selector.length === 0 && this.hasLoopingScenes() ){
                selector = scene_container.find(".scene:not(.sticky_scene):not(.popup_scene)").first();
            }
        }else if(scene === "PREV"){ // go to previous scene, if no previous go to first
            selector = this.currentScene.getSceneElement().prevAll(".scene:not(.sticky_scene):not(.popup_scene)").first();
            if(selector.length === 0 && this.hasLoopingScenes() ){
                selector =  scene_container.find(".scene:not(.sticky_scene):not(.popup_scene)").last();
            }
        }else if(scene === "FIRST"){ // go to next scene, if no next go to first
            selector = scene_container.find(".scene:not(.sticky_scene):not(.popup_scene)").first();
        }else{ // direct scene link.
            selector = this.runSelector("#"+this.completeSceneUUID(scene));
            if( selector.hasClass("sticky_scene")){
                console.warn("can't link to sticky scene");
                return false;
            }
        }

        if( selector.length === 0 ){
            return false;
        }

        if (selector[0].classList.contains("popup_scene")) {
            // setActivePopup looks the scene up by its full uuid; `scene` may be the bare id a host
            // passes to goToScene, which would silently match nothing
            this.setActivePopup({sceneId: this.completeSceneUUID(scene), mode: "open"}, currentTarget, dispatchEventUpdate);
        } else {
            // implicit close on scene navigation; the scene-change event below already covers it
            this.currentScene.isPopup && this.setActivePopup({mode: "close", duration: 0}, currentTarget, false);
            if (this.hasScrollingScenes) {
                if (this.sceneNavigationMode === "host") {
                    // The host moves the page to the scene, and that move reports the link.
                    Message.sendMessage({message: "scene_link", scene: selector.attr("id")}, this.spread_id);
                    return true;
                }
                send_update_event = this.scrollToScene(selector, {mode: this.sceneNavigationMode});
            }else{
                // the legacy CSS-class path has no mobile transitions; the gsap path
                // resolves the mobile effect (or "none") from the layout itself
                const skipAnimation = this.sceneNavigationMode !== "animated" ||
                    (this.active_layout === LAYOUT_MOBILE && !this.useGsapSceneTransitions());
                send_update_event = this.transitionToScene(selector, skipAnimation)
            }
        }

        //check if an update event should be send.
        if(send_update_event && dispatchEventUpdate ){
            UserEvents.sendEventUpdate("Link", "Scene", $(selector).data('label'), this.getSettings(), currentTarget );
        }
        return send_update_event;
    }

    /**
     * Scroll to the given scene (selector) after passing some tests
     *
     * @param {object|string} selector
     * @param {object} options {snap: boolean, bottom: boolean, duration: int}
     * @returns {boolean}
     */
    scrollToScene(selector, options = {}){
        if(this.maglr_scrolling){
            console.warn("Already scrolling", selector);
            return false;
        }

        let new_current = $(selector),
            same_current = new_current.is(".current_scene"),
            scrollable,
            current_scroll = this.getScrollTop(),
            new_scrolltop,
            new_offsettop
        ;

        // if( !options.snap && same_current ){
        //     console.warn("not scrolling to already current scene", selector);
        //     return false;
        // }

        if(options.rect){
            new_offsettop = Math.floor(options.rect.top);
        }else{
            new_offsettop = Math.floor(new_current[0].getBoundingClientRect().top);
        }

        //For mobile we need the mobile scale
        if( this.embeddedWithJsControl && this.embeddedType === CONSTANTS.EMBEDTYPES.FULLHEIGHT ){
            Message.sendEmbedMessage("SCROLL_TO", {
                scroll: Math.floor(new_current[0].getBoundingClientRect().top),
                browser: checkBrowser()
            });
            return true;
        }
        //subtract the interface offset.top to compensate for the extra scroll

        new_offsettop -= this.getInterfaceOffset().top + this.getScrollViewportTop();
        if(new_offsettop === 0){

            console.warn("not scrolling because scroller already at new_scrolltop");
            if( !options.suppress && ! same_current ){ // fallback for when scrolled precisely to the top of a scene.
                this.setCurrentSceneByUUID(new_current.attr("id"));
            }
            return false;
        }

        scrollable = this.getScrollContainer();

        new_scrolltop = new_offsettop + current_scroll;


        if(options.bottom){
            new_scrolltop += (new_current.outerHeight() - this.getWindowHeight());
        }

        if(current_scroll === new_scrolltop){
            console.warn("not scrolling because scroller already at new_scrolltop");
            if( !options.suppress && ! same_current ){ // fallback for when scrolled precisely to the top of a scene.
                this.setCurrentSceneByUUID(new_current.attr("id"));
            }
            return false;
        }

        if (options.mode === "direct") {
            scrollable.scrollTop(new_scrolltop);
            if (!options.suppress && !same_current) {
                this.setCurrentSceneByUUID(new_current.attr("id"));
            }
            return !same_current;
        }

        scrollable.animate({
                scrollTop: new_scrolltop
            }, {
                duration : options.duration || 400,
                start : () =>{
                    this.maglr_scrolling = true;
                },
                complete : () =>{
                    if( !options.suppress && ! same_current ){
                        this.setCurrentSceneByUUID(new_current.attr("id"));
                    }
                    this.maglr_scrolling = false;
                }
            }
        );

        return !same_current;
    }

    setScrollPosition(data){
        // console.log(data);
        if( this.scrolling_scenes ){
            let current_rect,
                to_be_current_scene,
                viewport = data.bottom - data.top,
                snapline = data.top + (viewport * 0.5)
            ;
            // check scrollPosition for active scene

            this.scenes.forEach((scene, i) => {
                current_rect = scene.getBoundingClientRect();
                if( current_rect.height > 0 ){

                    if( !to_be_current_scene && !scene.sticky ){
                        to_be_current_scene = scene;
                    }else{
                        let calc_top = Math.max(0, current_rect.top);

                        if( calc_top <= snapline){
                            to_be_current_scene = scene;
                        }
                    }
                }
            });

            if( ! to_be_current_scene){
                console.warn("no to be current scene.");
                return;
            }
            this.setCurrentScene(to_be_current_scene);
        }

        // if currentScene.pageType === longpage also update scrollPosition in currentScene
        if(this.currentScene.pagetype === CONSTANTS.PAGETYPES.LONGPAGE){
            let scene_element = this.currentScene.getSceneElement(true),
                offset = scene_element[0].offsetTop
            ;

            this.currentScene.longpageScrolled(scene_element, {
                top: data.top - offset,
                bottom: data.bottom - offset
            });
            this._setScrollDepth({
                top: data.top - offset,
                bottom: data.bottom - offset
            });
        }
    }

    /**
     * Transition (with a cool effect) to the given scene(selector)
     *
     * @param {object|string} selector
     * @returns {boolean}
     */
    transitionToScene(selector, skip_animation = false){
        if( this._inTransition() ){
            console.debug("Already animating. return false.");
            return false;
        }

        if (this.useGsapSceneTransitions()) {
            return this.sceneTransitions.transitionToScene(selector, skip_animation);
        }

        let new_current = this.runSelector(selector),
            old_current = this.runSelector(".current_scene"),
            transitionName = this.runSelector(".layout.desktop").data("scenestransition"),
            transitions = Spread.getSceneTransitions(transitionName, new_current.data('scene-index') < old_current.data('scene-index')),
            out_class = transitions.out,
            in_class = transitions.in
        ;

        if(new_current.length === 0){
            console.warn("transition to unknown scene", selector);
            return false;
        }
        if( new_current.is(old_current) ){
            console.warn("transition to already current scene", selector);
            return false;
        }

        this.pageTransitions = {
            old_ani_ended : false,
            new_ani_ended : false,
            isAnimating : true,
            inClass : in_class,
            outClass : out_class
        };


        this.shiftScrollToScene(this.currentScene);

        if(skip_animation || out_class === "page-transition-none"){
            this.onEndAnimation( old_current, new_current );
        }else{
            this.getLayoutContainer().classList.add("scenesInTransition");

            old_current.on( CONSTANTS.ANIMATION_DONE_EVENT, () => {
                old_current.off(CONSTANTS.ANIMATION_DONE_EVENT);
                this.pageTransitions.old_ani_ended = true;
                if( this.pageTransitions.new_ani_ended ) {
                    this.onEndAnimation( old_current, new_current );
                }
            } ).addClass(out_class);

            new_current.on( CONSTANTS.ANIMATION_DONE_EVENT, () => {
                new_current.off( CONSTANTS.ANIMATION_DONE_EVENT);
                this.pageTransitions.new_ani_ended = true;
                if( this.pageTransitions.old_ani_ended ) {
                    this.onEndAnimation( old_current, new_current );
                }
            } ).addClass(in_class);

        }

        return true;
    }

    /**
     *
     * @returns {boolean}
     */
    _inTransition(){
        return (this.pageTransitions && this.pageTransitions.isAnimating === true) || this.sceneTransitions.isAnimating();
    }

    useGsapSceneTransitions() {
        return this.gsapSceneTransitions;
    }

    /**
     *
     * @param old_current
     * @param new_current
     */
    onEndAnimation( old_current, new_current ) {

        old_current.removeClass( this.pageTransitions.outClass );
        new_current.removeClass( this.pageTransitions.inClass );

        this.setCurrentSceneByUUID(new_current.attr("id"));
        this.pageTransitions.isAnimating = false;

        if (this.active_layout === LAYOUT_DESKTOP) {
            this.getLayoutContainer().classList.remove("scenesInTransition");

            this.shiftScrollFromScene(this.currentScene);
        }
    }

    bindScroll(){
        let container = this.getScrollContainer(true);
        const eventNamespace = `${this.spread_id}-maglr`;

        //unbind scrollListener
        if(container){
            container.off(`scroll.${eventNamespace} touchmove.${eventNamespace}`);
        }
        //only bind scenebyscroll on more then 1 non-sticky scene
        if(this.scenes.length > 1 && !(this.scenes.length === 2 && this.scenes[0].sticky )){
            if( container ){
                container.one(`touchmove.${eventNamespace}`, (e) => {
                    this._isScrolled = true;
                });
                if (this.hasScrollingScenes) {
                    this.setIntersectionObserver();
                }
            }

            if (this.active_layout === LAYOUT_DESKTOP && !this.scrolling_scenes && this.mousewheel_enabled) {

                this.getSceneContainer(true)
                    .off(`wheel.${eventNamespace}`)
                    .on(`wheel.${eventNamespace}`,  (e) => {
                        // The embedding viewer owns the wheel while a cover is collapsed.
                        // Returning before preventDefault lets the outer edition keep its
                        // native document scroll without rebinding this runtime.
                        if (!this.wheelNavigationEnabled) {
                            return
                        }
                        if (this.status < CONSTANTS.STATUS.STARTED) {
                            console.log('SPREAD IS NOT ACTIVE YET!!!')
                            return
                        }
                        if (this.currentScene.isPopup === true) {
                            return
                        }
                        e.preventDefault()
                        const curTime = Date.now();

                        //time difference between the last scroll and the current one
                        //haven't they scrolled in a while or the first scroll?
                        //(enough to be consider a different scrolling action to scroll another section)
                        if( !this.prevTime || curTime - this.prevTime > 200){
                            //emptying the array, we dont care about old scrollings for our averages
                            this.scrollings = [];
                        }
                        this.prevTime = curTime;

                        //Limiting the array to 150 (lets not waste memory!)
                        if(this.scrollings.length > 149){
                            this.scrollings.shift();
                        }
                        this.scrollings.push(Math.abs(e.originalEvent.deltaY)); /*e.originalEvent.wheelDelta || */

                        if( !this._inTransition() ){

                            let averageEnd = this._getArrayAVG(this.scrollings, 10),
                                averageMiddle = this._getArrayAVG(this.scrollings, 70),
                                isAccelerating = averageEnd >= averageMiddle
                            ;

                            if(isAccelerating){
                                if( /*e.originalEvent.wheelDelta > 0 ||*/ e.originalEvent.deltaY > 0) {
                                    this.sceneLink("NEXT");
                                } else {
                                    this.sceneLink("PREV");
                                }
                            }
                        }
                    })
                ;
            }
        }

        if (this.hasScrollingScenes && container) {
            this.scrollListener = new ScrollListener({
                scene : container,
                scroller: container,
                getOffset: this.scrollContext.element ? () => this.scrollContext.offsetOf(this.getSpreadElement(false)) : undefined,
                scrollerHeight: this.getWindowHeight()
            });

            // if( this.scenes.some((scene) => {return scene.hasStickies()}) ){
                // let scrolltop = this.getScrollTop();
                // this.scenes.forEach((scene) => {
                //     scene.initPositions(scrolltop);
                // });
            // }

            this.scrollListener.on("scrolled", (event) => {
                let offsetTop = this.getInterfaceOffset().top;
                event.data.top += offsetTop;
                event.data.bottom += offsetTop;
                // this._setStickiesActive(event.data);

                this._setScrollDepth(event.data);
            })
            if (this.scrolling_scenes && this.sceneSnap && this.active_layout === LAYOUT_DESKTOP) {
                // only snap when not injected/embedded or when embedded as singlePage
                if (!this.options.injected || (this.options.injected && this.options.injected.singlePage)) {
                    this.scrollListener.on("scrolled", debounce(
                        () => {
                            this.snapToScene()
                        }, 250, false)
                    )
                }
            }

            // }else if(this.scenes.length > 1 && !(this.scenes.length === 2 && this.scenes[0].sticky )){ // more then 1 non-sticky scene
            // //listen for the number of scenes activated
            // this.setSceneDepth();
        }

        this.scrollingSceneEffects.init();
    }

    unbindScroll(){
        let container = this.getScrollContainer(true);
        const eventNamespace = `${this.spread_id}-maglr`;
        if(container){
            container.off(`scroll.${eventNamespace} touchmove.${eventNamespace}`);
        }
        this.getSceneContainer(true).off(`wheel.${eventNamespace}`);

        this.scrollListener && this.scrollListener.destroy().removeAllListeners("scrolled");
        this.scrollingSceneEffects.kill();

    }

    /**
     * get the average of the last `number` of array values of arr
     * @param arr
     * @param number
     * @returns {number}
     * @private
     */
    _getArrayAVG(arr, number){
        //taking `number` elements from the end to make the average
        let lastElements = arr.slice(number * -1);
        //get the average by summing the values and divide by length
        return Math.ceil(lastElements.reduce( (a,b) => a + b, 0) / lastElements.length)
    }

    setIntersectionObserver(){
        // remove the previous observer if existent
        if(this.intersectionObserver){
            this.intersectionObserver.disconnect();
        }

        let options = {
                rootMargin: `-${this.getWindowHeight()*0.1}px 0px`,
            }
        ;

        if(this.scrollContext.element){
            options.root = this.getScrollContainer(true)[0];
        }
        if (window.MaglrPreviewPlugin) {
            options.root = document
        }

        this.intersectionObserver = new IntersectionObserver( (entries) => {
            //loop entries array, during scroll its only 1 entry but on pageLoad there can be multiple entries
            entries.forEach((entry) => {
                if (this.status === CONSTANTS.STATUS.STARTED) {
                    this.setSceneInViewport(entry.target.getAttribute('id'), entry.isIntersecting);
                    this.setSceneLinkActive()
                }
            })
        }, options);

        this.scenes.forEach((scene) => {
            if( !scene.sticky ){
                // start observing
                this.intersectionObserver.observe(scene.getSceneElement(false));
            }

        })
    }

    setSceneInViewport(uuid, bool){
        this.scenes[this.scenesMap.get(uuid)].inViewport = bool;
    }

    snapToScene(){
        let viewportHeight = this.getWindowHeight(),
            interfaceOffset = this.getInterfaceOffset(),
            viewportBoundries = {
                top    : this.getScrollViewportTop() + interfaceOffset.top,
                bottom : this.getScrollViewportTop() + interfaceOffset.top + viewportHeight
            },
            snapline = this.getScrollViewportTop() + (viewportHeight * 0.5) + interfaceOffset.top,
            current_rect,
            newCurrent,
            newCurrentRect,
            scenesInViewport = 0
        ;

        this.scenes.forEach((scene, i) => {
            if (!scene.sticky && !scene.isPopup) {
                current_rect = scene.getBoundingClientRect();
                current_rect.top < viewportBoundries.bottom && current_rect.bottom > viewportBoundries.top && scenesInViewport++;
                if(current_rect.top <= snapline){
                    newCurrent = scene;
                    newCurrentRect = current_rect;
                }
            }
        });

        if(this.sceneSnap && scenesInViewport > 1){
            if(newCurrentRect.height >= viewportHeight && newCurrentRect.top <= viewportBoundries.top && newCurrentRect.bottom >= viewportBoundries.bottom){
                console.log('Current scene already snapped to top, bottom or somewhere in between.')
            } else {
                let snapToBottom = newCurrentRect.bottom <= viewportBoundries.bottom && newCurrentRect.height >= viewportHeight;
                this.scrollToScene(newCurrent.getSceneElement(), {snap: true, bottom : snapToBottom, rect: newCurrentRect});
            }
        }
    }

    static getSceneTransitions(transition, reverse){
        let transitions = {};
        switch(transition){
            case "none":
                transitions.out = 'page-transition-none';
                transitions.in = 'page-transition-none';
                break;
            case "scaleDown":
                transitions.out = 'pt-page-scaleDown';
                transitions.in = 'pt-page-moveFromBottom pt-page-ontop';
                if(reverse){
                    transitions.in = 'pt-page-moveFromTop pt-page-ontop';
                }
                break;
            case "moveScale":
                transitions.out = 'pt-page-moveToTop pt-page-ontop';
                transitions.in = 'pt-page-scaleUp';
                if(reverse){
                    transitions.out = 'pt-page-moveToBottom pt-page-ontop';
                }
                break;
            case "scaleUpDown":
                transitions.out = 'pt-page-scaleDownCenter';
                transitions.in = 'pt-page-scaleUpCenter pt-page-delay400';
                break;
            case "differentEasing":
                transitions.out = 'pt-page-moveToTopEasing pt-page-ontop';
                transitions.in = 'pt-page-moveFromBottom';
                if(reverse){
                    transitions.out = 'pt-page-moveToBottomEasing pt-page-ontop';
                    transitions.in = 'pt-page-moveFromTop';
                }
                break;
            case "fadeMove":
                transitions.out = 'pt-page-fadeOut';
                transitions.in = 'pt-page-moveFromBottom pt-page-ontop';
                if(reverse){
                    transitions.in = 'pt-page-moveFromTop  pt-page-ontop';
                }
                break;
            case "push":
                transitions.out = 'pt-page-rotatePushTop';
                transitions.in = 'pt-page-moveFromBottom';
                if(reverse){
                    transitions.out = 'pt-page-rotatePushBottom';
                    transitions.in = 'pt-page-moveFromTop';
                }
                break;
            case "flip":
                transitions.out = 'pt-page-flipOutBottom';
                transitions.in = 'pt-page-flipInTop pt-page-delay500';
                if(reverse){
                    transitions.out = 'pt-page-flipOutTop';
                    transitions.in = 'pt-page-flipInBottom pt-page-delay500';
                }
                break;
            case "fall":
                transitions.out = 'pt-page-rotateFall pt-page-ontop';
                transitions.in = 'pt-page-scaleUp';
                break;
            case "room":
                transitions.out = 'pt-page-rotateRoomBottomOut pt-page-ontop';
                transitions.in = 'pt-page-rotateRoomBottomIn';
                if(reverse){
                    transitions.out = 'pt-page-rotateRoomTopOut pt-page-ontop';
                    transitions.in = 'pt-page-rotateRoomTopIn';
                }
                break;
            case "glue":
                transitions.out = 'pt-page-rotateBottomSideFirst';
                transitions.in = 'pt-page-moveFromBottom pt-page-delay200 pt-page-ontop';
                if(reverse){
                    transitions.out = 'pt-page-rotateTopSideFirst';
                    transitions.in = 'pt-page-moveFromTop pt-page-delay200 pt-page-ontop';
                }
                break;
            case "cube":
                transitions.out = 'pt-page-rotateCubeTopOut pt-page-ontop';
                transitions.in = 'pt-page-rotateCubeTopIn';
                if(reverse){
                    transitions.out = 'pt-page-rotateCubeBottomOut';
                    transitions.in = 'pt-page-rotateCubeBottomIn';
                }
                break;
            case "fold":
                transitions.out = 'pt-page-rotateFoldBottom';
                transitions.in = 'pt-page-moveFromTopFade';
                if(reverse){
                    transitions.out = 'pt-page-rotateFoldTop';
                    transitions.in = 'pt-page-moveFromBottomFade';
                }
                break;
            case "crossFade":
                transitions.out = 'pt-page-fadeOut pt-page-ontop';
                transitions.in = 'pt-page-fadeIn';
                break;



            case "scroll":
            default:
                transitions.out = 'pt-page-moveToTop';
                transitions.in = 'pt-page-moveFromBottom';
                if(reverse){
                    transitions.out = 'pt-page-moveToBottom';
                    transitions.in = 'pt-page-moveFromTop';
                }
                break;
        }

        transitions.in += ' transition_scene';
        return transitions;
    }

    runSelector(selector){
        let currentSpread = this.getSpreadElement(true);
        if(currentSpread.is(selector)){
            return currentSpread;
        }else{
            return currentSpread.find(selector);
        }
    }

    getSpreadElement(as_jquery = true){
        if(as_jquery){
            return $( this.node, this.root )
        }else{
            return this.node;
        }
    }

    /**
     * Whether this spread renders inside the editor's live preview. Set per spread through the
     * `editorPreview` option (the maglr-spread element passes it from MaglrSpread.globalConfig);
     * the window flag is what the classic preview document (html.mustache) sets, kept as the
     * fallback until that preview is retired.
     *
     * @returns {boolean}
     */
    isEditorPreview(){
        return this.options.editorPreview === true || window.MaglrEditorPreview === true;
    }

    /**
     * Editor preview only: keep plain anchors from navigating the preview away. One capturing
     * listener on the root (the shadow root, or the document) covers anchors added later too.
     * Copying `href` into `data-external-link` hands the click to the delegated link handler
     * ({@see Scene#bindButtons}), which reports it to the host as an `external_link` message.
     */
    bindEditorPreviewLinks(){
        this.editorPreviewClickHandler = (event) => {
            const anchor = event.composedPath().find((node) => node instanceof HTMLAnchorElement);
            if (!anchor || !anchor.hasAttribute("href")) {
                return;
            }
            event.preventDefault();
            if (!anchor.hasAttribute("data-external-link")) {
                anchor.setAttribute("data-external-link", anchor.getAttribute("href"));
            }
        };
        this.root.addEventListener("click", this.editorPreviewClickHandler, true);
    }

    getLayoutContainer(){
        let selector = ".layout.desktop";
        if(this.active_layout === LAYOUT_MOBILE){
            selector = ".layout.mobile";
        }
        return this.getSpreadElement(false).querySelector(selector);
    }

    getSettings(){
        return this.settings;
    }


    /**
     * Start the given spread_audio file by letting the "frontend" know which file to play. If spread_audio is false tell
     * it to stop the spread_audio. The editor preview has no frontend around it, so there the spread plays the
     * track itself (see playLocalSpreadAudio). Only the option, not the window flag: the classic preview document
     * still has the editor playing the track from these messages, like bindEditorPreviewLinks above.
     */
    startSpreadAudio(spread_audio){
        if (this.options.editorPreview === true) {
            this.playLocalSpreadAudio(spread_audio);
            return;
        }
        if( spread_audio ){
            Message.sendMessage( {message: "SPREADAUDIO_START", data: spread_audio}, this.spread_id );
        }else{
            Message.sendMessage( {message: "SPREADAUDIO_STOP"}, this.spread_id );
        }
    }

    /**
     * Editor preview only: loop the spread audio in this document, muted while in-spread audio or video plays
     * (see setInSpreadAudioPlaying). A published page hands the track to the frontend instead.
     *
     * @param {string[]|false} spread_audio sources as the payload lists them, or false to stop
     */
    playLocalSpreadAudio(spread_audio){
        this.stopLocalSpreadAudio();
        if (!spread_audio || spread_audio.length === 0) {
            return;
        }
        this.localSpreadAudio = new Howl({
            src: spread_audio,
            loop: true,
            mute: this.inSpreadAudioPlaying === true,
        });
        this.localSpreadAudio.play();
    }

    stopLocalSpreadAudio(){
        if (this.localSpreadAudio) {
            this.localSpreadAudio.unload();
            this.localSpreadAudio = null;
        }
    }

    /**
     * A scene reports that in-spread audio/video started or stopped; the local spread audio (editor preview)
     * ducks for it, the frontend gets the INSPREADAUDIO_* message for the same purpose.
     *
     * @param {boolean} playing
     */
    setInSpreadAudioPlaying(playing){
        this.inSpreadAudioPlaying = playing === true;
        this.localSpreadAudio?.mute(this.inSpreadAudioPlaying);
    }

    getBoundingClientRect(){
        return this.getSpreadElement(false).getBoundingClientRect();
    }

    getActiveLayout(){
        return this.active_layout;
    }

    getTimeline(){
        return this.currentScene.animations.mainTL;
    }

    completeSceneUUID(scene){
        if( scene.indexOf("scene_") === -1 ){
            return constructSceneUUID(scene, this.active_layout, this.spread_id)
        }
        return scene;
    }

    /**
     * Set that this page is embedded with Js control in the embedding page and set the given embed type.
     */
    setEmbedded(data){
        this.embeddedWithJsControl = true;
        this.embeddedType = data.type;
        this.embeddedSizeSetting = data.maxsize || false;
        if(this.embeddedType === CONSTANTS.EMBEDTYPES.FULLHEIGHT){
            $(".embed.singlePage").addClass(this.embeddedType);
        }
    }

    /**
     * Set the viewport size. This is used for embedded pages with JS control
     * @param {{width, height}}viewport
     */
    setViewportSize(viewport){
        let resize = false;
        if(this.windowWidth !== viewport.width){
            this.windowWidth = viewport.width;
            resize = true;
        }
        if(this.windowHeight !== viewport.height){
            this.windowHeight = viewport.height;
            resize = true;
        }
        if(resize || this.embeddedType === CONSTANTS.EMBEDTYPES.STATIC){
            this.safemargin.onWindowResizeEvent(null, {force:true})
        }
    }

    // _setStickiesActive(data){
        // switch (this.status) {
        //     case CONSTANTS.STATUS.STARTED:
        //         this.scenes.forEach((scene) => {
        //             if( ! scene.sticky && scene.hasStickies() ){
        //                 scene.setStickiesActive(data);
        //             }
        //         });
        //         break;
        //     case CONSTANTS.STATUS.SHOWN:
        //     case CONSTANTS.STATUS.READY:
        //         // this.scenes.forEach((scene) => {
        //         //     if( ! scene.sticky && scene.hasStickies() ){
        //         //         scene.activateSticky("0%");
        //         //     }
        //         // });
        //         break;
        // }
    // }

    setSceneSizeData(uuid, size_data){
        let index = this.scenesMap.get(uuid);
        if(index !== undefined){
            this.scenes[index].setSceneSizeData(size_data)
        }
    }

    /**
     * Get an option by name
     * @param {String} name
     * @returns {*}
     */
    getOption(name){
        return this.options[name];
    }

    setGlobalData(data){
        this.globalData = data;

        return this;
    }

    getGlobalData(){
        return this.globalData;
    }

    setInterfaceSettings(settings){
        this.interfaceSettings = settings;
        return this;
    }

    getInterfaceSettings(){
        return this.interfaceSettings || {type: false, height: 0, width: 0};
    }

    getInterfaceOffset(){
        let interfaceSettings = this.getInterfaceSettings(),
            spreadOffset = this.getSpreadOffset(),
            offset = {
                top: 0 + spreadOffset.top,
                right: 0 + spreadOffset.right,
                bottom: 0 + spreadOffset.bottom,
                left: 0 + spreadOffset.left,
            }
        ;
        switch (interfaceSettings.type) {
            case "top":
                offset.top += interfaceSettings.height;
                break;
            case "right":
                offset.right += interfaceSettings.width;
                break;
            case "bottom":
                offset.bottom += interfaceSettings.height;
                break;
            case "left":
                offset.left += interfaceSettings.width;
                break;
        }
        return offset;
    }

    getSpreadOffset(){
        return this._spreadOffset || {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
        };
    }

    setSpreadOffset(offset){
        this._spreadOffset = {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
        };
        Object.keys(offset).map((key) => {
            this._spreadOffset[key] += offset[key];
        });
        this.clearSizes();
    }


    shiftScrollToScene(scene){
        if(scene.pagetype === CONSTANTS.PAGETYPES.LONGPAGE){
            //shift the current scroll from the body to the scenes maglrspread
            const scroll = this.getScrollTop()
            if(scroll >= 0){
                this.scrollContext.scrollTo(0);

                scene.previousScrollPosition = scroll;
                scene.getMaglrSpread().css("transform", `translateY(-${scroll}px)`)
            }
        }
    }

    shiftScrollFromScene(scene){
        if(scene.pagetype === CONSTANTS.PAGETYPES.LONGPAGE){
            const scroll = scene.previousScrollPosition || 0
            this.scrollContext.scrollTo(scroll);

            if(scene.previousScrollPosition >= 0){
                scene.getMaglrSpread().css("transform", "")
            }
        }
    }


    isScrolled(){
        return this._isScrolled;
    }

    _setScrollDepth(data){
        if(!this._scrollHeight){
            return
        }
        // maxDepth = max( ceil(bottom / 1%), maxDepth)
        this._scrollDepth = Math.max(Math.ceil(data.bottom / this._scrollHeight.onePercent), this._scrollDepth || 0);

        //if scrollDepth > 90% we round to 100%
        if(this._scrollDepth > 90){
            this._scrollDepth = 100;
        }
    }

    reportScrollDepth(){
        if(this.status >= CONSTANTS.STATUS.STOPPED){
            const {layout, depth} = this.getScrollDepth()
            UserEvents.sendEventUpdate('Scroll-depth', `Position-${layout}`, depth, this.getSettings() );
        }
    }

    getScrollDepth() {
        const layout = {1:'desktop', 2: 'mobile'}[this.active_layout]
        let depth;
        //Get the total scene-count of the non-sticky scenes
        let total = this.scenes.length;
        if (total === 0) {
            return;
        }
        if (this.scenes[0].sticky) {
            total -= 1;
        }
        if (this.hasScrollingScenes) {
            if (this._scrollDepth !== undefined) {
                //put the scrollDepth in the correct quartile box by dividing by 25 so it leaves us with 5 options (0 - 4) which
                //resolve to the indexes of the nice numbers
                depth = [0,25,50,75,100][Math.floor(this._scrollDepth / 25)];
            } else {
                if (total === 1 && this.scenes[this.scenes.length -1].pagetype !== CONSTANTS.PAGETYPES.LONGPAGE) {
                    depth = 100
                } else {
                    depth = 0
                }
            }
        }else{
            //No scrollListener active because not mobile and not scrolling_scenes

            // Calculate the amount of non-sticky scenes that have been started
            const startedScenes = this.scenes.reduce((acc, cur) => {
                if(!cur.sticky && cur.status >= CONSTANTS.STATUS.STOPPED){
                    acc++
                }
                return acc;
            }, 0);

            //depth = (100 / total scenes) * started scenes
            depth = Math.ceil((100 / total) * startedScenes);
        }
        return {layout, depth}
    }

    getTriggerCookieConsent(trigger = true){
        if(this._triggerCookieConsent && trigger){
            Message.sendMessage("COOKIE_CONSENT", this.spread_id);
        }
        return this._triggerCookieConsent || false;
    }

    setTriggerCookieConsent(bool){
        this._triggerCookieConsent = bool;
    }

    getCookieConcented(){
        return window.MaglrCookieConsented === undefined || window.MaglrCookieConsented;
    }

    getScrollLock(){
        if (this.active_layout === LAYOUT_DESKTOP) {
            if(this.scrolling_scenes){
                // for scrolling scenes check scene count (and sticky) and type not longpage
                if(this.scenes.length === 1 || (this.scenes.length === 2 && this.scenes[0].sticky )){
                    if(this.scenes[this.scenes.length - 1].pagetype !== CONSTANTS.PAGETYPES.LONGPAGE){
                        return true;
                    }
                }
            }else{
                //for transitioning scenes check the current scene for type not longpage
                if(this.currentScene.pagetype !== CONSTANTS.PAGETYPES.LONGPAGE){
                    return true;
                }
            }
        }

        return false;
    }

    enterA11yMode () {
        if (!this.a11yMode) {
            this.scenes.forEach((scene )=>{
                scene.videos && scene.videos.setA11yMode()
            });
            this.a11yMode = true
        }
    }

    async multiActionLinkEventHandler (event) {
        event.stopImmediatePropagation();
        const actions = $(event.currentTarget).data("multi-action-link");

        await this.multiActionLink(actions, event.currentTarget)
        UserEvents.sendEventUpdate('Link', 'Multiple actions', 'Multiple actions', this.getSettings(), event.currentTarget);
    }

    async multiActionLink (actions, currentTarget = {}) {
        // loop needed to execute actions
        let events = ''
        for await (const value of this.executeMultiActions([...actions], currentTarget)) {
            events += value
        }
    }

    executeMultiActions = async function* (actions, currentTarget) {

        const index = actions.findIndex(({type}) => type === 'internal-link')
        const otherSpreadActions = index > -1 ? actions.splice(index) : []

        for (let i = 0; i < actions.length; i++) {
            const {type, data} = actions[i]
            switch (type) {
                case 'scene-link':
                    // yield await this.sceneLink(data.scene, currentTarget, false)
                    yield await new Promise((resolve /*, reject*/) => {
                        this.sceneLink(data.scene, currentTarget, false)
                        setTimeout(() => resolve(true), this.scrolling_scenes ? 500 : 1000);
                    });
                    break
                case 'popup-link':
                    yield await this.setActivePopup(data, currentTarget, false)
                    break
                case 'scrollto-link':
                    // yield await Links.scrollTo(this, data, this.getInterfaceOffset(), currentTarget )
                    yield await new Promise((resolve /*, reject*/) => {
                        Links.scrollTo(this, data, this.getInterfaceOffset(), currentTarget )
                        setTimeout(() => resolve(true), data.speed);
                    });
                    break
                case 'togglestate':
                    const sceneUuid = this.runSelector(`#${data.elementIds[0]}`).closest('.scene').attr('id')
                    const scene = this.getSceneByUuid(sceneUuid) ?? this.currentScene
                    yield await scene.toggleState({togglestate: data}, currentTarget, false)
                    break
                case 'goToState': {
                        const sceneUuid = this.runSelector(`#${data.elementIds[0]}`).closest('.scene').attr('id')
                        const scene = this.getSceneByUuid(sceneUuid) ?? this.currentScene
                        yield await scene.goToState(data)
                    }
                    break
                case 'action-link':
                    const trigger = async () => {
                        this.runSelector("#" + data.uuid).trigger(CONSTANTS.EVENT_ELEMENT_ACTION, {action: data.action})
                    }
                    yield await trigger()
                    break
                case 'sleep':
                    yield await new Promise((resolve /*, reject*/) => {
                        setTimeout(() => resolve(true), data.sleep ?? 2000);
                    });
                    break
                case 'highlight':
                    yield await new Promise((resolve /*, reject*/) => {
                        this.removeHighlight()
                        const {uuid, taskId} = data
                        const element = document.getElementById(uuid)
                        element.classList.add('comments-highlight')
                        element.setAttribute('data-taskid', taskId)

                        resolve(true)
                    });
                    break
            }
            console.log(`${type} done`)
        }

        if (otherSpreadActions.length > 0) {
            console.log("other actions to do by other spread:", otherSpreadActions)
            Message.sendMessage({actions: otherSpreadActions, message: "multiActionLink"}, this.spread_id);
        }
    }

    async goToAndHighlightElement({spreadId, uuid, taskId}) {
        const actions = []
        if (this.spread_id !== spreadId) {
            actions.push(
                {type: 'internal-link', data: {spread_id: spreadId, issue_id: this.settings._spread_globals.issue_id}},
                {type: 'highlight', data: {spreadId, taskId, uuid}}
            )
        } else {
            const element = document.getElementById(uuid)
            const scene = element.closest('.scene')
            if (this.currentScene.uuid !== scene.id) {
                actions.push(
                    {type: 'scene-link', data: {scene: scene.id}}
                )
            }

            if (!isElementInViewport(element)) {
                actions.push(
                    {type: 'scrollto-link', data: {scene: scene.id, element: uuid, speed: 400}}
                )
            }

            actions.push(
                {type: 'highlight', data: {taskId, uuid}}
            )
        }

        await this.multiActionLink(actions)
    }

    removeHighlight() {
        const nodeList = document.querySelectorAll('.comments-highlight')
        nodeList.forEach(node => node.classList.remove('comments-highlight'))
    }

    async setActivePopup({sceneId, mode, duration}, currentTarget, dispatchEventUpdate = true) {
        const scene = mode === 'close'
            ? this.currentScene.isPopup ? this.currentScene : this.getSceneByUuid(this.runSelector(".scene.popup_scene.scene_active")[0]?.id)
            : this.getSceneByUuid(sceneId)

        if (!scene || !scene.isPopup) {
            return
        }

        const sceneElement = scene.getSceneElement(false)
        const isActive = sceneElement.classList.contains('scene_active');

        if (dispatchEventUpdate) {
            UserEvents.sendEventUpdate('Popup', isActive ? 'Close' : 'Open', sceneElement.dataset.label, this.getSettings(), currentTarget);
        }

        return isActive
            ? await this.sceneTransitions.closePopup(sceneElement, scene, duration)
            : await this.sceneTransitions.openPopup(sceneElement, scene, duration);
    }

    hasLoopingScenes() {
        const layoutClass = this.active_layout === LAYOUT_MOBILE ? 'mobile' : 'desktop';
        return Boolean(
            this.runSelector(`.layout.${layoutClass}`).data("loopscenes")
            ?? this.runSelector(`.layout.desktop`).data("loopscenes") // backwards compatible with old (pre summer '26) data
        );
    }
}
